import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SKIP_DUPLICATE_CHECK } from '@insite-store360/auth';
import {
  Observable,
  catchError,
  filter,
  map,
  of,
  switchMap,
  take,
  tap,
  throwError,
  timer,
} from 'rxjs';
import type {
  AsyncExportApiConfig,
  AsyncExportErrorContext,
  CsvQueuedExportOptions,
} from './async-export.types';
import type { ExcelExportProgressSlot } from './excel-export-progress.service';

const POLL_INTERVAL_MS = 3000;
const RETRYABLE_STATUS = new Set([408, 502, 503, 504]);

@Injectable({
  providedIn: 'root',
})
export class AsyncExportService {
  constructor(private readonly http: HttpClient) {}

  /** True when background export queue (Redis) is down — fall back to direct download. */
  isQueueUnavailable(err: unknown): boolean {
    const status =
      (err as { status?: number })?.status ??
      (err as { error?: { statusCode?: number } })?.error?.statusCode;
    if (status === 503) {
      return true;
    }
    const text = JSON.stringify(err ?? '').toLowerCase();
    return (
      text.includes('startup nodes') ||
      text.includes('redis unreachable') ||
      text.includes('export queue')
    );
  }

  formatProgressLabel(phase: string, exportingLabel: string): string {
    if (phase === 'starting') {
      return 'Loading export data';
    }
    if (phase === 'finalize') {
      return 'Saving export file';
    }
    if (phase === 'upload') {
      return 'Uploading export';
    }
    return exportingLabel;
  }

  cancelJob(apiBaseUrl: string, jobId: string): Observable<unknown> {
    const skipDup = new HttpContext().set(SKIP_DUPLICATE_CHECK, true);
    return this.http.post(
      `${apiBaseUrl}/async-export/jobs/${jobId}/cancel`,
      {},
      { context: skipDup },
    );
  }

  /**
   * Start job → poll status → download file → mark downloaded.
   * Used by domain services (raw-data, survey-service).
   */
  runAsyncExport(
    api: AsyncExportApiConfig,
    params: Record<string, unknown>,
    progress: ExcelExportProgressSlot,
    buildStartBody?: (body: Record<string, unknown>) => Record<string, unknown>,
  ): Observable<void> {
    progress.setProgress('Starting export', 0);
    const skipDup = new HttpContext().set(SKIP_DUPLICATE_CHECK, true);
    const startBody = buildStartBody ? buildStartBody(params) : params;

    return this.http
      .post<{ result?: { jobId?: string }; jobId?: string }>(
        `${api.apiBaseUrl}/async-export/start`,
        startBody,
        { context: skipDup },
      )
      .pipe(
        map((r) => r?.result ?? r),
        map((inner) => {
          const jobId = inner?.jobId;
          if (!jobId) {
            throw new Error('No job id returned');
          }
          return { jobId: String(jobId) };
        }),
        tap(({ jobId }) => progress.setJobId(jobId)),
        switchMap(({ jobId }) =>
          timer(0, POLL_INTERVAL_MS).pipe(
            switchMap(() =>
              this.http
                .get<Record<string, unknown>>(
                  `${api.apiBaseUrl}/async-export/jobs/${jobId}`,
                  { context: skipDup },
                )
                .pipe(
                  map((r) => (r?.['result'] ?? r) as Record<string, unknown>),
                  catchError((err) => {
                    const status =
                      (err as { status?: number })?.status ??
                      (err as { statusCode?: number })?.statusCode ??
                      (err as { error?: { statusCode?: number } })?.error
                        ?.statusCode;
                    if (status != null && RETRYABLE_STATUS.has(Number(status))) {
                      return of(null);
                    }
                    return throwError(() => err);
                  }),
                ),
            ),
            filter((s): s is Record<string, unknown> => s != null),
            tap((s) => {
              const pct = typeof s['progress'] === 'number' ? s['progress'] : 0;
              const processed = Number(s['processedRows'] ?? 0);
              const total = Number(s['totalRows'] ?? 0);
              const phase = String(s['progressPhase'] ?? 'rows');
              progress.setProgress(
                this.formatProgressLabel(phase, api.exportingLabel),
                pct,
                { processed, total, phase },
              );
            }),
            filter(
              (s) =>
                s['state'] === 'completed' ||
                s['state'] === 'failed' ||
                s['state'] === 'cancelled',
            ),
            take(1),
            switchMap((s) => {
              if (s['state'] === 'cancelled') {
                progress.clear();
                return of(undefined);
              }
              if (s['state'] === 'failed') {
                progress.clear();
                return throwError(
                  () => new Error(String(s['error'] ?? 'Export failed')),
                );
              }
              progress.setProgress('Downloading file', 99, {
                processed: Number(s['processedRows'] ?? 0),
                total: Number(s['totalRows'] ?? 0),
                phase: 'upload',
              });
              return this.downloadExportFile(api, jobId, {
                fileName: s['fileName'] as string | undefined,
              }).pipe(
                switchMap((resolvedFileName) =>
                  this.http
                    .post(
                      `${api.apiBaseUrl}/async-export/jobs/${jobId}/downloaded`,
                      {},
                      { context: skipDup },
                    )
                    .pipe(map(() => resolvedFileName)),
                ),
                tap((resolvedFileName) =>
                  progress.markDownloadComplete(resolvedFileName),
                ),
              );
            }),
          ),
        ),
        map(() => undefined),
        catchError((err) => {
          progress.clear();
          return throwError(() => err);
        }),
      );
  }

  downloadExportFile(
    api: AsyncExportApiConfig,
    jobId: string,
    summary: { fileName?: string },
  ): Observable<string> {
    const remoteName = (summary.fileName ?? '').toLowerCase();
    const fallback = api.defaultFileName;
    const fileName = remoteName.endsWith('.csv')
      ? summary.fileName!
      : remoteName.endsWith('.zip')
        ? summary.fileName!
        : remoteName.endsWith('.xlsx')
          ? summary.fileName!
          : fallback;
    const skipDup = new HttpContext().set(SKIP_DUPLICATE_CHECK, true);
    const headers = new HttpHeaders().set('isBlob', 'true');
    return this.http
      .get(`${api.apiBaseUrl}/async-export/jobs/${jobId}/download`, {
        responseType: 'blob',
        observe: 'response',
        context: skipDup,
        headers,
      })
      .pipe(
        map((response) => {
          const blob = response.body;
          if (!blob) {
            throw new Error('Download failed');
          }
          const disposition = response.headers.get('content-disposition') || '';
          const match = disposition.match(/filename="?([^"]+)"?/i);
          const resolvedFileName = (match?.[1] || fileName).trim() || fileName;
          const lowerName = resolvedFileName.toLowerCase();
          const resolvedMime = lowerName.endsWith('.csv')
            ? 'text/csv'
            : lowerName.endsWith('.zip')
              ? 'application/zip'
              : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
          const blobType = (blob.type || '').toLowerCase();
          const typed =
            !blobType || blobType === 'application/octet-stream'
              ? new Blob([blob], { type: resolvedMime })
              : blob;
          const objectUrl = URL.createObjectURL(typed);
          const anchor = document.createElement('a');
          anchor.href = objectUrl;
          anchor.download = resolvedFileName;
          anchor.rel = 'noopener noreferrer';
          document.body.appendChild(anchor);
          anchor.click();
          // Revoking immediately cancels the save in Chrome and Safari.
          window.setTimeout(() => {
            anchor.remove();
            URL.revokeObjectURL(objectUrl);
          }, 1500);
          return resolvedFileName;
        }),
      );
  }

  /**
   * Shared export button flow: optional embedded-image direct download,
   * otherwise queued CSV with header progress.
   */
  runCsvQueuedExport(options: CsvQueuedExportOptions): boolean {
    if (options.withImageExport) {
      if (options.validateWithImageExport && !options.validateWithImageExport()) {
        return false;
      }
      options.toast.info(
        options.embeddedImagesInfoMessage ??
          'Embedded images use direct Excel download. Background export uses CSV with image links.',
      );
      this.runDirectBlobExport(options);
      return true;
    }

    const slot = options.excelExportProgress.tryCreateSlot();
    if (!slot) {
      options.toast.error(
        'You are not allowed to start a third export. Maximum 2 exports at a time.',
      );
      return false;
    }

    options.excelExportProgress.registerCancelHandler(slot.id, () => {
      const jobId = options.excelExportProgress.getJobId(slot.id);
      options.excelExportProgress.cancelPolling(slot.id);
      if (jobId) {
        this.cancelJob(options.api.apiBaseUrl, jobId).subscribe({
          error: () => options.toast.error('Could not cancel export'),
        });
      }
      slot.clear();
    });

    options.excelExportProgress.runExport(
      slot,
      this.runAsyncExport(
        options.api,
        options.params,
        slot,
        options.buildStartBody,
      ),
      {
        next: () => options.onComplete?.(),
        error: (err) =>
          this.handleAsyncExportError({
            err,
            toast: options.toast,
            directDownload$: options.directDownload$,
            directFileName: options.directFileName,
            excelExportProgress: options.excelExportProgress,
            params: options.params,
            onFallbackDirect: () => this.runDirectBlobExport(options),
            onAfterError: options.onComplete,
            fallbackToDirectOnConcurrencyLimit:
              options.fallbackToDirectOnConcurrencyLimit,
          }),
      },
    );
    return true;
  }

  handleAsyncExportError(ctx: AsyncExportErrorContext): void {
    if (this.isQueueUnavailable(ctx.err)) {
      ctx.toast.warning(
        'Background export queue unavailable; downloading directly. Keep this page open until the file saves.',
      );
      ctx.onFallbackDirect?.();
      return;
    }

    const status = this.extractHttpStatus(ctx.err);
    const message = this.extractHttpErrorMessage(ctx.err);
    if (status === 408) {
      ctx.toast.error(
        message ||
          'Export service timed out while starting. Please retry in a moment.',
      );
    } else if (status === 429) {
      if (ctx.fallbackToDirectOnConcurrencyLimit) {
        ctx.toast.warning(
          'Background export queue is busy; downloading Excel directly. Keep this tab open until the file saves.',
        );
        ctx.onFallbackDirect?.();
        return;
      }
      ctx.toast.error(
        message ||
          'You already have 2 exports running. Wait for one to finish or cancel it from the header.',
      );
    } else if (
      message.toLowerCase().includes('cannot export') &&
      message.toLowerCase().includes('rows')
    ) {
      ctx.toast.error(message, undefined, {
        timeOut: 10_000,
        extendedTimeOut: 4_000,
      });
    } else {
      ctx.toast.error(message || 'Export failed');
    }
    ctx.onAfterError?.();
  }

  private extractHttpStatus(err: unknown): number | undefined {
    const e = err as {
      status?: number;
      statusCode?: number;
      error?: { status?: number; statusCode?: number };
    };
    const status = e?.status ?? e?.statusCode ?? e?.error?.status ?? e?.error?.statusCode;
    return typeof status === 'number' ? status : undefined;
  }

  private extractHttpErrorMessage(err: unknown): string {
    const e = err as {
      message?: string;
      error?: string | { message?: string | string[] };
    };
    let message = '';
    if (typeof e?.error === 'string') {
      message = e.error;
    } else if (e?.error && typeof e.error === 'object' && e.error.message) {
      message = Array.isArray(e.error.message)
        ? e.error.message.join(', ')
        : String(e.error.message);
    } else {
      message = e?.message ?? '';
    }
    const trimmed = message.trim();
    if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
      try {
        const parsed = JSON.parse(trimmed) as { message?: unknown };
        if (typeof parsed?.message === 'string' && parsed.message.trim()) {
          return parsed.message;
        }
      } catch {
        // Keep the original text when it is not JSON.
      }
    }
    return message;
  }

  private runDirectBlobExport(options: CsvQueuedExportOptions): void {
    const slot = options.excelExportProgress.tryCreateSlot();
    if (!slot) {
      options.toast.error('Only 2 exports can run at a time');
      return;
    }
    options.onDirectDownloadStart?.();
    options.excelExportProgress.runBlobExport(
      slot,
      options.directDownload$,
      {
        label: options.api.exportingLabel,
        fileName: options.directFileName,
        onComplete: () => {
          options.onDirectDownloadEnd?.();
          options.onComplete?.();
        },
        onError: () => {
          options.onDirectDownloadEnd?.();
          options.toast.error('Direct download failed');
          options.onComplete?.();
        },
      },
    );
  }
}
